import React from "react";
import { useNavigate } from "react-router";
import { Drafts, NotificationsActive, NotificationsNoneOutlined, SettingsOutlined } from "@mui/icons-material";
import { Box, Button } from "@mui/material";
import { Inbox, Notification, ReactInboxAppearance } from "@novu/react";
import { enqueueSnackbar } from "notistack";

import { useGetNotificationSubscriberQuery } from "fond/api/userSlice";
import theme from "fond/theme";
import ErrorBoundary from "fond/widgets/ErrorBoundary";

import { isCommentNotification, isLegacyRequestLicenseNotification, isMentionNotification, isRequestLicenseNotification } from "./helper";
import { renderAvatar, renderBell, renderBody, renderSubject } from "./renderFunctions";
import { CommentData, RequestLicenseData, TypedNotification } from "./types";

import { InboxContainer } from "./NotificationInbox.styles";

const novuApplicationIdentifier = process.env?.REACT_APP_NOVU_APP_ID;

const inboxAppearance: ReactInboxAppearance = {
  variables: {
    colorPrimary: theme.palette.primary.main,
  },
  elements: {
    preferencesHeader__icon: {
      color: "green",
    },
    channelIconContainer: {
      fontSize: "1rem",
      lineHeight: 1,
    },
    notificationPrimaryAction__button: {},
  },
  icons: {
    cogs: () => <SettingsOutlined color="action" />,
    bell: () => <NotificationsNoneOutlined />,
    inApp: () => <NotificationsActive sx={{ fontSize: "1rem" }} />,
    email: () => <Drafts sx={{ fontSize: "1rem" }} />,
  },
};

const NotificationInbox: React.FC = () => {
  const { data } = useGetNotificationSubscriberQuery();
  const navigate = useNavigate();

  if (!data || !novuApplicationIdentifier) {
    return null;
  }

  const navigateToComment = (notification: TypedNotification<CommentData>) => {
    const { CommentID, ProjectID } = notification.data;
    window.location.href = `/project/${ProjectID}/comment/${CommentID}`;
  };

  const navigateToLicenseRequest = (notification: TypedNotification<RequestLicenseData>) => {
    const { UserID, UserEmail } = notification.data;
    if (!UserID || !UserEmail) {
      enqueueSnackbar("Notification is missing user information.", {
        variant: "error",
        action: <Button onClick={() => navigate("/settings/users/list")}>Go to user list</Button>,
      });
      return;
    }
    const urlParams = new URLSearchParams({ allocateLicense: "true", userId: UserID, userEmail: UserEmail });
    navigate(`/settings/users/list?${urlParams}`);
  };

  const onNotificationClick = (notification: Notification) => {
    if (isRequestLicenseNotification(notification)) {
      navigateToLicenseRequest(notification);
      return;
    }
    if (isCommentNotification(notification) || isMentionNotification(notification)) {
      navigateToComment(notification);
      return;
    }

    if (isLegacyRequestLicenseNotification(notification)) {
      // legacy notifications don't have their user info so we can only navigate to the user list.
      navigate("/settings/users/list");
      return;
    }

    // for legacy notifications that have a URL in the payload we can still handle their request
    if (notification?.redirect?.url) {
      // Novu has a bug where they HTML Encode & to &amp; in the redirect URL, so we need to replace it back here before navigating
      window.location.href = notification.redirect.url.replaceAll("&amp;", "&");
    }
  };

  // Called when the 'action' (read: button) is clicked on a notification. This behaves identically
  // to just clicking the notification itself, however we could implement more advanced logic here.
  const onPrimaryActionClick = (notification: Notification) => {
    onNotificationClick(notification);
  };

  return (
    <ErrorBoundary>
      <Box display="flex">
        <InboxContainer>
          <Inbox
            appearance={inboxAppearance}
            applicationIdentifier={novuApplicationIdentifier}
            onNotificationClick={onNotificationClick}
            onPrimaryActionClick={onPrimaryActionClick}
            renderAvatar={renderAvatar}
            renderBell={renderBell}
            renderBody={renderBody}
            renderSubject={renderSubject}
            subscriber={data.Subscriber.ID}
            subscriberHash={data.Subscriber.Hash}
          />
        </InboxContainer>
      </Box>
    </ErrorBoundary>
  );
};

export default NotificationInbox;
